import { createApp, defineAsyncComponent } from 'vue'
import { createPinia } from 'pinia'
import piniaPersist from 'pinia-plugin-persist'
import VueDragscroll from 'vue-dragscroll'
import { createWebHistory, createRouter } from 'vue-router'
import VScrollLock from 'v-scroll-lock'
import VueTheMask from 'vue-the-mask'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import toast from 'izitoast'
import VueSignaturePad from 'vue-signature-pad'
import 'babel-polyfill'
// misc plugins
import './plugins/jquery.min.js'
import './bootstrap.js'
import './plugins/bootstrap.min.js'
import './helpers.js'
import './seint.js'

const CheckboxInput = defineAsyncComponent(() => import('@components/_new/CheckboxInput.vue'))
const EmblaCarousel = defineAsyncComponent(() => import('@components/_new/EmblaCarousel.vue'))
const EmblaSlide = defineAsyncComponent(() => import('@components/_new/EmblaSlide.vue'))
const EmblaSwimlane = defineAsyncComponent(() => import('@components/_new/EmblaSwimlane.vue'))
const EmblaSwimlaneCard = defineAsyncComponent(() => import('@components/_new/EmblaSwimlaneCard.vue'))
const HeadlessAccordion = defineAsyncComponent(() => import('@components/_new/HeadlessAccordion.vue'))
const HeadlessModal = defineAsyncComponent(() => import('@components/_new/HeadlessModal.vue'))
const HeadlessTabs = defineAsyncComponent(() => import('@components/_new/HeadlessTabs.vue'))
const RadioInput = defineAsyncComponent(() => import('@components/_new/RadioInput.vue'))
const TextMarquee = defineAsyncComponent(() => import('@components/_new/TextMarquee.vue'))
const BagFooter = defineAsyncComponent(() => import('@components/Bag/Footer.vue'))
const BagFreeShippingReward = defineAsyncComponent(() => import('@components/Bag/FreeShippingReward.vue'))
const BagItemQuantity = defineAsyncComponent(() => import('@components/Bag/ItemQuantity.vue'))
const BagItemRewards = defineAsyncComponent(() => import('@components/Bag/ItemRewards.vue'))
const BagItems = defineAsyncComponent(() => import('@components/Bag/Items.vue'))
const BagPaletteCreditReward = defineAsyncComponent(() => import('@components/Bag/PaletteCreditReward.vue'))
const ColorMatch = defineAsyncComponent(() => import('@components/ColorMatch/index.vue'))
const ColorMatchAddToBag = defineAsyncComponent(() => import('@components/ColorMatch/AddToBag.vue'))
const ColorMatchBulkAddToBag = defineAsyncComponent(() => import('@components/ColorMatch/AddBulkToBag.vue'))
const ColorMatchShareLink = defineAsyncComponent(() => import('@components/ColorMatch/ShareLink.vue'))
const AffiliateBanner = defineAsyncComponent(() => import('@components/AffiliateBanner.vue'))
const AgreementModal = defineAsyncComponent(() => import('@components/AgreementModal.vue'))
const AnchorScroll = defineAsyncComponent(() => import('@components/AnchorScroll.vue'))
const Braintree = defineAsyncComponent(() => import('@components/Braintree.vue'))
const Carousel = defineAsyncComponent(() => import('@components/Carousel.vue'))
const ContactForm = defineAsyncComponent(() => import('@components/ContactForm.vue'))
const CookieNotice = defineAsyncComponent(() => import('@components/CookieNotice.vue'))
const EmailSubscribe = defineAsyncComponent(() => import('@components/EmailSubscribe.vue'))
const EmailUnsubscribeForm = defineAsyncComponent(() => import('@components/EmailUnsubscribe.vue'))
const ErrorMessages = defineAsyncComponent(() => import('@components/ErrorMessages.vue'))
const EventRegistrationEditForm = defineAsyncComponent(() => import('@components/EventRegistrationEditForm.vue'))
const EventRegistrationForm = defineAsyncComponent(() => import('@components/EventRegistrationForm.vue'))
const FormAddress = defineAsyncComponent(() => import('@components/FormAddress.vue'))
const GenericModal = defineAsyncComponent(() => import('@components/GenericModal.vue'))
const HeroBanner = defineAsyncComponent(() => import('@components/HeroBanner.vue'))
const HeroBannerModal = defineAsyncComponent(() => import('@components/HeroBannerModal.vue'))
const HorizontalCtaItems = defineAsyncComponent(() => import('@components/HorizontalCtaItems.vue'))
const HumanizeRelativeDate = defineAsyncComponent(() => import('@components/HumanizeRelativeDate.vue'))
const ImageUpload = defineAsyncComponent(() => import('@components/ImageUpload.vue'))
const SvgVue = defineAsyncComponent(() => import('@components/SvgVue.vue'))
const LabelTag = defineAsyncComponent(() => import('@components/LabelTag.vue'))
const LazyPaginator = defineAsyncComponent(() => import('@components/LazyPaginator.vue'))
const Loader = defineAsyncComponent(() => import('@components/Loader.vue'))
const MarketChoice = defineAsyncComponent(() => import('@components/MarketChoice.vue'))
const ProductList = defineAsyncComponent(() => import('@components/ProductLists/ProductList.vue'))
const ProductListCard = defineAsyncComponent(() => import('@components/ProductLists/ProductListCard.vue'))
const ProductListForm = defineAsyncComponent(() => import('@components/ProductLists/ProductListForm.vue'))
const ProductListHeader = defineAsyncComponent(() => import('@components/ProductLists/ProductListHeader.vue'))
const ProductListItems = defineAsyncComponent(() => import('@components/ProductLists/ProductListItems.vue'))
const ProductListLoginForm = defineAsyncComponent(() => import('@components/ProductLists/ProductListLoginForm.vue'))
const ProductListChoice = defineAsyncComponent(() => import('@components/ProductLists/ProductListChoice.vue'))
const ProductListSearch = defineAsyncComponent(() => import('@components/ProductLists/ProductListSearch.vue'))
const ProductListBulkAdd = defineAsyncComponent(() => import('@components/ProductLists/ProductListBulkAdd.vue'))
const MeiliArtistSearch = defineAsyncComponent(() => import('@components/Meili/Artist/Search.vue'))
const MeiliProductSearchBtn = defineAsyncComponent(() => import('@components/Meili/Product/SearchBtn.vue'))
const MeiliProductSearch = defineAsyncComponent(() => import('@components/Meili/Product/Search.vue'))
const MeiliProductListSearch = defineAsyncComponent(() => import('@components/Meili/ProductList/Search.vue'))
const MediaCarousel = defineAsyncComponent(() => import('@components/MediaCarousel.vue'))
const MobileMenu = defineAsyncComponent(() => import('@components/MobileMenu.vue'))
const OrderConfirmation = defineAsyncComponent(() => import('@components/OrderConfirmation.vue'))
const PayLaterMessage = defineAsyncComponent(() => import('@components/PayLaterMessage.vue'))
const AddPaymentMethodForm = defineAsyncComponent(() => import('@components/Profile/AddPaymentMethodForm.vue'))
const EditAddressForm = defineAsyncComponent(() => import('@components/Profile/EditAddressForm.vue'))
const EditDobForm = defineAsyncComponent(() => import('@components/Profile/EditDobForm.vue'))
const EditEmailForm = defineAsyncComponent(() => import('@components/Profile/EditEmailForm.vue'))
const EditLoginForm = defineAsyncComponent(() => import('@components/Profile/EditLoginForm.vue'))
const EditNameForm = defineAsyncComponent(() => import('@components/Profile/EditNameForm.vue'))
const EditPaymentMethodForm = defineAsyncComponent(() => import('@components/Profile/EditPaymentMethodForm.vue'))
const EditPhoneForm = defineAsyncComponent(() => import('@components/Profile/EditPhoneForm.vue'))
const EditTaxNameForm = defineAsyncComponent(() => import('@components/Profile/EditTaxNameForm.vue'))
const VerifyAddressModal = defineAsyncComponent(() => import('@components/Profile/VerifyAddressModal.vue'))
const AddToBag = defineAsyncComponent(() => import('@components/Returns/AddToBag.vue'))
const ContinueBtn = defineAsyncComponent(() => import('@components/Returns/ContinueBtn.vue'))
const ItemOptions = defineAsyncComponent(() => import('@components/Returns/ItemOptions.vue'))
const ItemSelection = defineAsyncComponent(() => import('@components/Returns/ItemSelection.vue'))
const ReturnMessage = defineAsyncComponent(() => import('@components/Returns/ReturnMessage.vue'))
import PriceInfo from '@components/PriceInfo.vue'
const RewardsProgram = defineAsyncComponent(() => import('@components/RewardsProgram.vue'))
const RewardsProgramTable = defineAsyncComponent(() => import('@components/RewardsProgramTable.vue'))
const PrintMe = defineAsyncComponent(() => import('@components/PrintMe.vue'))
const SubscribeEmail = defineAsyncComponent(() => import('@components/SubscribeEmail.vue'))
const SuperKey = defineAsyncComponent(() => import('@components/SuperKey.vue'))
const SwitchInput = defineAsyncComponent(() => import('@components/SwitchInput.vue'))
const ToggleValue = defineAsyncComponent(() => import('@components/ToggleValue.vue'))
const TrainingVideos = defineAsyncComponent(() => import('@components/TrainingVideos.vue'))
const UserMenu = defineAsyncComponent(() => import('@components/UserMenu.vue'))
const CheckoutForm = defineAsyncComponent(() => import('@views/checkout/CheckoutForm.vue'))
const EnrollmentAccountForm = defineAsyncComponent(() => import('@components/Enrollment/AccountForm.vue'))
const EnrollmentAffiliateLinkForm = defineAsyncComponent(() => import('@components/Enrollment/AffiliateLinkForm.vue'))
const EnrollmentCheckout = defineAsyncComponent(() => import('@components/Enrollment/Checkout.vue'))
const EnrollmentKitSelection = defineAsyncComponent(() => import('@components/Enrollment/KitSelection.vue'))
const ItemDetailsModal = defineAsyncComponent(() => import('@components/Loyalty/ItemDetailsModal.vue'))
const LoyaltyModal = defineAsyncComponent(() => import('@components/Loyalty/LoyaltyModal.vue'))
const PointsHistory = defineAsyncComponent(() => import('@components/Loyalty/PointsHistory.vue'))
const RedeemBtn = defineAsyncComponent(() => import('@components/Loyalty/RedeemBtn.vue'))
const ReferralLink = defineAsyncComponent(() => import('@components/Loyalty/ReferralLink.vue'))
const PbAddToBag = defineAsyncComponent(() => import('@components/PaletteBuilder/AddToBag.vue'))
const PbCategoryCount = defineAsyncComponent(() => import('@components/PaletteBuilder/CategoryCount.vue'))
const PbDesign = defineAsyncComponent(() => import('@components/PaletteBuilder/Design.vue'))
const PbSpace = defineAsyncComponent(() => import('@components/PaletteBuilder/Space.vue'))
const PbImagePreloader = defineAsyncComponent(() => import('@components/PaletteBuilder/ImagePreloader.vue'))
const PbItemPreloader = defineAsyncComponent(() => import('@components/PaletteBuilder/ItemPreloader.vue'))
const PbItemQuantity = defineAsyncComponent(() => import('@components/PaletteBuilder/ItemQuantity.vue'))
const PbLoading = defineAsyncComponent(() => import('@components/PaletteBuilder/Loading.vue'))
const PbOptions = defineAsyncComponent(() => import('@components/PaletteBuilder/Options.vue'))
const PbPalettes = defineAsyncComponent(() => import('@components/PaletteBuilder/Palettes.vue'))
const PbRenderedPalette = defineAsyncComponent(() => import('@components/PaletteBuilder/RenderedPalette.vue'))
import PbShareLink from '@components/PaletteBuilder/ShareLink.vue'
const AddToBagBtn = defineAsyncComponent(() => import('@components/Product/AddToBagBtn.vue'))
const AddToListBtn = defineAsyncComponent(() => import('@components/Product/AddToListBtn.vue'))
const ProductImageCarousel = defineAsyncComponent(() => import('@components/Product/ProductImageCarousel.vue'))
import ProductDetails from '@components/Product/ProductDetails.vue'
const VariantAddToBagBtn = defineAsyncComponent(() => import('@components/Product/VariantAddToBagBtn.vue'))
const VariantDescription = defineAsyncComponent(() => import('@components/Product/VariantDescription.vue'))
const VariantImages = defineAsyncComponent(() => import('@components/Product/VariantImages.vue'))
const VariantIngredients = defineAsyncComponent(() => import('@components/Product/VariantIngredients.vue'))
const VariantPrice = defineAsyncComponent(() => import('@components/Product/VariantPrice.vue'))
const VariantSaleMessage = defineAsyncComponent(() => import('@components/Product/VariantSaleMessage.vue'))
const VariantSelector = defineAsyncComponent(() => import('@components/Product/VariantSelector.vue'))
const PaidCommissionSearch = defineAsyncComponent(() => import('@views/office/PaidCommissionSearch.vue'))
const CustomerRegistrationForm = defineAsyncComponent(() => import('@views/registration/CustomerRegistrationForm.vue'))
const SocialLinksForm = defineAsyncComponent(() => import('@views/office/SocialLinksForm.vue'))
const UpdateBillingForm = defineAsyncComponent(() => import('@views/office/UpdateBillingForm.vue'))
const SelfCheckoutForm = defineAsyncComponent(() => import('@components/SelfCheckoutForm.vue'))
const ShoppingBag = defineAsyncComponent(() => import('@components/ShoppingBag.vue'))
const Signature = defineAsyncComponent(() => import('@components/Signature.vue'))

import App from '@views/App.vue'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const app = createApp()

const router = createRouter({
	history: createWebHistory('/'),
	routes: [
		{
			path: '/:languageCode/shop/retail/item/:itemId/:itemSlug?/',
			name: 'retail-product-layout',
			component: ProductDetails,
		},
		{
			path: '/:languageCode/shop/office/item/:itemId/:itemSlug?/',
			name: 'office-product-layout',
			component: ProductDetails,
		},
		{
			path: '/:languageCode/shop/retail/palette-builder/:hash/',
			name: 'retail-palette-builder-layout',
			component: PbShareLink,
		},
		{
			path: '/:languageCode/shop/office/palette-builder/:hash/',
			name: 'office-palette-builder-layout',
			component: PbShareLink,
		},
	],
})
app.use(router)

app.use(VScrollLock)
app.use(VueDragscroll)
app.use(VueSignaturePad)
app.use(VueTheMask)

app.component('app', App)
app.component('checkbox-input', CheckboxInput)
app.component('embla-carousel', EmblaCarousel)
app.component('embla-slide', EmblaSlide)
app.component('embla-swimlane', EmblaSwimlane)
app.component('embla-swimlane-card', EmblaSwimlaneCard)
app.component('headless-accordion', HeadlessAccordion)
app.component('headless-modal', HeadlessModal)
app.component('headless-tabs', HeadlessTabs)
app.component('radio-input', RadioInput)
app.component('text-marquee', TextMarquee)
app.component('bag-footer', BagFooter)
app.component('bag-free-shipping-reward', BagFreeShippingReward)
app.component('bag-item-quantity', BagItemQuantity)
app.component('bag-item-rewards', BagItemRewards)
app.component('bag-items', BagItems)
app.component('bag-palette-credit-reward', BagPaletteCreditReward)
app.component('color-match', ColorMatch)
app.component('color-match-add-to-bag', ColorMatchAddToBag)
app.component('color-match-bulk-add-to-bag', ColorMatchBulkAddToBag)
app.component('color-match-share-link', ColorMatchShareLink)
app.component('anchor-scroll', AnchorScroll)
app.component('affiliate-banner', AffiliateBanner)
app.component('agreement-modal', AgreementModal)
app.component('braintree', Braintree)
app.component('carousel', Carousel)
app.component('contact-form', ContactForm)
app.component('cookie-notice', CookieNotice)
app.component('email-subscribe', EmailSubscribe)
app.component('email-unsubscribe-form', EmailUnsubscribeForm)
app.component('error-messages', ErrorMessages)
app.component('event-registration-edit-form', EventRegistrationEditForm)
app.component('event-registration-form', EventRegistrationForm)
app.component('form-address', FormAddress)
app.component('generic-modal', GenericModal)
app.component('hero-banner', HeroBanner)
app.component('hero-banner-modal', HeroBannerModal)
app.component('horizontal-cta-items', HorizontalCtaItems)
app.component('humanize-relative-date', HumanizeRelativeDate)
app.component('image-upload', ImageUpload)
app.component('svg-vue', SvgVue)
app.component('label-tag', LabelTag)
app.component('lazy-paginator', LazyPaginator)
app.component('loader', Loader)
app.component('market-choice', MarketChoice)
app.component('add-payment-method-form', AddPaymentMethodForm)
app.component('edit-address-form', EditAddressForm)
app.component('edit-dob-form', EditDobForm)
app.component('edit-email-form', EditEmailForm)
app.component('edit-login-form', EditLoginForm)
app.component('edit-name-form', EditNameForm)
app.component('edit-payment-method-form', EditPaymentMethodForm)
app.component('edit-phone-form', EditPhoneForm)
app.component('edit-tax-name-form', EditTaxNameForm)
app.component('verify-address-modal', VerifyAddressModal)
app.component('add-to-bag', AddToBag)
app.component('continue-btn', ContinueBtn)
app.component('item-options', ItemOptions)
app.component('item-selection', ItemSelection)
app.component('return-message', ReturnMessage)
app.component('price-info', PriceInfo)
app.component('rewards-program', RewardsProgram)
app.component('rewards-program-table', RewardsProgramTable)
app.component('print-me', PrintMe)
app.component('product-list', ProductList)
app.component('product-list-card', ProductListCard)
app.component('product-list-form', ProductListForm)
app.component('product-list-header', ProductListHeader)
app.component('product-list-items', ProductListItems)
app.component('product-list-login-form', ProductListLoginForm)
app.component('product-list-choice', ProductListChoice)
app.component('product-list-search', ProductListSearch)
app.component('product-list-bulk-add', ProductListBulkAdd)
app.component('meili-artist-search', MeiliArtistSearch)
app.component('meili-product-search-btn', MeiliProductSearchBtn)
app.component('meili-product-search', MeiliProductSearch)
app.component('meili-product-list-search', MeiliProductListSearch)
app.component('media-carousel', MediaCarousel)
app.component('mobile-menu', MobileMenu)
app.component('order-confirmation', OrderConfirmation)
app.component('pay-later-message', PayLaterMessage)
app.component('super-key', SuperKey)
app.component('switch-input', SwitchInput)
app.component('subscribe-email', SubscribeEmail)
app.component('toggleValue', ToggleValue)
app.component('training-videos', TrainingVideos)
app.component('user-menu', UserMenu)
app.component('checkout-form', CheckoutForm)
app.component('enrollment-account-form', EnrollmentAccountForm)
app.component('enrollment-affiliate-link-form', EnrollmentAffiliateLinkForm)
app.component('enrollment-checkout', EnrollmentCheckout)
app.component('enrollment-kit-selection', EnrollmentKitSelection)
app.component('item-details-modal', ItemDetailsModal)
app.component('loyalty-modal', LoyaltyModal)
app.component('points-history', PointsHistory)
app.component('redeem-btn', RedeemBtn)
app.component('referral-link', ReferralLink)
app.component('pb-design', PbDesign)
app.component('pb-add-to-bag', PbAddToBag)
app.component('pb-category-count', PbCategoryCount)
app.component('pb-space', PbSpace)
app.component('pb-image-preloader', PbImagePreloader)
app.component('pb-item-preloader', PbItemPreloader)
app.component('pb-item-quantity', PbItemQuantity)
app.component('pb-loading', PbLoading)
app.component('pb-options', PbOptions)
app.component('pb-palettes', PbPalettes)
app.component('pb-rendered-palette', PbRenderedPalette)
app.component('pb-share-link', PbShareLink)
app.component('add-to-bag-btn', AddToBagBtn)
app.component('add-to-list-btn', AddToListBtn)
app.component('product-image-carousel', ProductImageCarousel)
app.component('product-details', ProductDetails)
app.component('variant-add-to-bag-btn', VariantAddToBagBtn)
app.component('variant-description', VariantDescription)
app.component('variant-images', VariantImages)
app.component('variant-ingredients', VariantIngredients)
app.component('variant-price', VariantPrice)
app.component('variant-sale-message', VariantSaleMessage)
app.component('variant-selector', VariantSelector)
app.component('paid-commission-search', PaidCommissionSearch)
app.component('customer-registration-form', CustomerRegistrationForm)
app.component('social-links-form', SocialLinksForm)
app.component('update-billing-form', UpdateBillingForm)
app.component('self-checkout-form', SelfCheckoutForm)
app.component('shopping-bag', ShoppingBag)
app.component('signature', Signature)

app.config.globalProperties.$toast = toast
const assetBucketBaseUrl = import.meta.env.VITE_ASSET_BUCKET_BASE_URL
app.config.globalProperties.$assetBucketBaseUrl = assetBucketBaseUrl

app.directive('click-outside', {
	mounted(el, binding) {
		el.clickOutsideEvent = function (event) {
			if (!(el === event.target || el.contains(event.target))) {
				binding.value(event, el)
			}
		}
		document.body.addEventListener('click', el.clickOutsideEvent)
	},
	unmounted(el) {
		document.body.removeEventListener('click', el.clickOutsideEvent)
	},
})

const pinia = createPinia()
pinia.use(piniaPersist)
app.use(pinia)

app.mount('#app')
